/**
 * Google Drive / OneDrive / SharePoint → Files, server-side (F-DMS).
 *
 * The provider pickers only hand back item ids; the backend fetches the bytes
 * with the user's stored integration token and files them in Files:
 *
 *   POST /integrations/<provider>/import
 *     { items: [{ id, drive_id?, name? }], folder_uuid? }
 *   → { success, data: { files: [{ uuid, name, size, mime_type }] }, errors?: [{ id, name, error }] }
 *   → 409 { code: 'NOT_CONNECTED' } when the provider was never connected
 *
 * SharePoint goes through `onedrive` (the picker gives a drive id).
 */

import apis from '../../../utils/apis';
import type { ExternalPickedItem, ExternalProvider, PickedFile } from './filesPickerTypes';

export class IntegrationNotConnectedError extends Error {
  readonly code = 'NOT_CONNECTED';
  constructor(readonly provider: ExternalProvider) {
    super(`${provider} is not connected`);
    this.name = 'IntegrationNotConnectedError';
  }
}

export const isNotConnectedError = (error: any): boolean => {
  if (!error) return false;
  if (error instanceof IntegrationNotConnectedError || error.code === 'NOT_CONNECTED') return true;
  const data = error?.response?.data;
  const code = data?.code || data?.error?.code || data?.error_code;
  return code === 'NOT_CONNECTED' || (error?.response?.status === 409 && !code);
};

export interface ExternalImportError {
  id: string;
  name?: string;
  error: string;
}

export interface ExternalImportResult {
  files: PickedFile[];
  errors: ExternalImportError[];
}

export async function importExternalItems(
  provider: ExternalProvider,
  items: ExternalPickedItem[],
  folderUuid?: string,
): Promise<ExternalImportResult> {
  if (!items.length) return { files: [], errors: [] };
  const body: Record<string, unknown> = {
    items: items.map((item) => ({
      id: item.id,
      ...(item.drive_id ? { drive_id: item.drive_id } : {}),
      ...(item.name ? { name: item.name } : {}),
    })),
  };
  if (folderUuid) body.folder_uuid = folderUuid;

  try {
    const response = await apis.post(`/integrations/${provider}/import`, body);
    const payload = response?.data || {};
    const files: any[] = payload?.data?.files || [];
    return {
      files: files.map((f) => ({
        uuid: f.uuid,
        name: f.name,
        size: f.size ?? undefined,
        mime_type: f.mime_type ?? undefined,
        source: provider,
        folder_uuid: f.folder_uuid ?? folderUuid ?? undefined,
      })),
      errors: Array.isArray(payload?.errors) ? payload.errors : [],
    };
  } catch (error: any) {
    if (isNotConnectedError(error)) throw new IntegrationNotConnectedError(provider);
    throw error;
  }
}

export type IntegrationStatus = Record<ExternalProvider, boolean>;

/** One read of `GET /integrations/auth/status`. Anything but a clean answer reads as "not connected". */
export async function fetchIntegrationStatus(): Promise<IntegrationStatus> {
  try {
    const response = await apis.get('/integrations/auth/status');
    const data = response?.data || {};
    return { gdrive: !!data.gdrive, onedrive: !!data.onedrive };
  } catch {
    return { gdrive: false, onedrive: false };
  }
}
